/**
 * MarketIntelligence Component
 * Comprehensive market intelligence for sellers including valuation, comparable deals, and insights
 */

import { useState, useEffect } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  DollarSign,
  Flame,
  Lightbulb,
  Target,
  Zap,
  BarChart3,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Building2,
  RefreshCw,
  AlertCircle,
  Clock,
  Sparkles,
  Bell,
  Activity,
  Users,
  Calendar,
  Beaker,
} from 'lucide-react';
import { sellersAPI, dealIntelligenceAPI, catalystsAPI } from '../../services/api';
import { Link } from 'react-router-dom';

export default function MarketIntelligence({ sellerId }) {
  const [data, setData] = useState(null);
  const [marketPulse, setMarketPulse] = useState(null);
  const [catalysts, setCatalysts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);

  useEffect(() => {
    if (!sellerId) return;
    fetchData();
  }, [sellerId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch market pulse, deal intelligence, and catalysts in parallel
      const [pulseResponse, intelligenceResponse, catalystsResponse] = await Promise.all([
        sellersAPI.getMarketPulse(sellerId).catch(() => null),
        dealIntelligenceAPI.getIntelligence(sellerId).catch(() => null),
        catalystsAPI.getForSeller(sellerId).catch(() => null),
      ]);

      if (pulseResponse?.data?.success) {
        setMarketPulse(pulseResponse.data.data);
      }
      if (intelligenceResponse?.data?.success) {
        setData(intelligenceResponse.data.data);
      }
      if (catalystsResponse?.data?.success) {
        setCatalysts(catalystsResponse.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch market intelligence:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      const response = await dealIntelligenceAPI.refresh(sellerId);
      if (response.data?.success) {
        setData(response.data.data);
      }
    } catch (err) {
      console.error('Failed to refresh intelligence:', err);
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="animate-pulse space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
            <div className="h-5 bg-gray-200 rounded w-1/3"></div>
          </div>
          <div className="h-24 bg-gray-100 rounded-lg"></div>
          <div className="h-16 bg-gray-100 rounded-lg"></div>
          <div className="h-16 bg-gray-100 rounded-lg"></div>
        </div>
      </div>
    );
  }

  // Show empty state if no data at all
  if (!data && !marketPulse) {
    return (
      <div className="bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 rounded-xl border border-emerald-100 p-5">
        <div className="text-center py-6">
          <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <DollarSign className="w-6 h-6 text-emerald-500" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-1">Market Intelligence</h3>
          <p className="text-sm text-gray-500 mb-4">
            Add assets to your profile to see valuation estimates
          </p>
          <Link
            to="/seller/profile"
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors"
          >
            Complete Profile
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  const valuation = data?.valuation;
  // Filter out entries with invalid scores (null, undefined, NaN)
  const isValidScore = (score) => typeof score === 'number' && !isNaN(score) && score > 0;
  const comparableDeals = (data?.comparableDeals || []).filter(d => isValidScore(d.similarityScore));
  const insights = data?.insights || [];
  const recommendedBuyers = (data?.recommendedBuyers || []).filter(b => isValidScore(b.matchScore));
  const marketContext = data?.marketContext;
  // New data
  const dealAlerts = data?.dealAlerts || [];
  const valuationTrends = data?.valuationTrends;
  const buyerAssetMatches = (data?.buyerAssetMatches || []).filter(m => isValidScore(m.matchScore));
  // Profile completeness
  const profileCompleteness = data?.profileCompleteness;

  // Get trending areas from market pulse
  const hotTherapeutics = marketPulse?.trendingTherapeutics?.filter(t => t.isHot) || [];
  const hotPlatforms = marketPulse?.trendingPlatforms?.filter(p => p.isHot) || [];
  const allTrending = [
    ...hotTherapeutics.map(t => ({ ...t, type: 'therapeutic' })),
    ...hotPlatforms.map(p => ({ ...p, type: 'platform' }))
  ];

  return (
    <div className="bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 rounded-xl border border-emerald-100 overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-emerald-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-r from-emerald-500 to-green-500 rounded-lg flex items-center justify-center">
            <DollarSign className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="font-semibold text-gray-900">Market Intelligence</h2>
            <p className="text-xs text-gray-500">Company-level valuation based on your overall portfolio</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {marketContext?.trend && (
            <TrendIndicator trend={marketContext.trend} trendPct={marketContext.trendPct} />
          )}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-colors"
            title="Refresh data"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-5 space-y-4">
        {/* Profile Completeness Warning */}
        {profileCompleteness && !profileCompleteness.isComplete && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800">
                  Complete your profile for better matches
                </p>
                <p className="text-xs text-amber-600 mt-0.5">
                  {profileCompleteness.message}
                </p>
                <Link
                  to="/seller/profile"
                  className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-amber-700 hover:text-amber-800"
                >
                  Add asset details
                  <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
              <span className="text-xs font-medium text-amber-600">
                {profileCompleteness.completenessScore}%
              </span>
            </div>
          </div>
        )}

        {/* Deal Alerts - Real-time market activity */}
        {dealAlerts.length > 0 && (
          <DealAlertsSection
            alerts={dealAlerts}
            expanded={expandedSection === 'alerts'}
            onToggle={() => setExpandedSection(expandedSection === 'alerts' ? null : 'alerts')}
          />
        )}

        {/* Valuation Trends */}
        {valuationTrends && valuationTrends.trend !== 'insufficient_data' && (
          <ValuationTrendCard trend={valuationTrends} />
        )}

        {/* Buyer-Asset Matches */}
        {buyerAssetMatches.length > 0 && (
          <BuyerAssetMatchSection
            matches={buyerAssetMatches}
            expanded={expandedSection === 'buyerMatches'}
            onToggle={() => setExpandedSection(expandedSection === 'buyerMatches' ? null : 'buyerMatches')}
          />
        )}

        {/* Valuation Card */}
        {valuation && valuation.confidence !== 'insufficient_data' ? (
          <ValuationCard valuation={valuation} />
        ) : (
          <div className="bg-white rounded-lg p-4 border border-gray-100">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900">Limited Comparable Data</p>
                <p className="text-xs text-gray-500 mt-1">
                  Add more details to your assets for better valuation estimates
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Comparable Deals */}
        {comparableDeals.length > 0 && (
          <ComparableDealsSection
            deals={comparableDeals}
            expanded={expandedSection === 'deals'}
            onToggle={() => setExpandedSection(expandedSection === 'deals' ? null : 'deals')}
          />
        )}

        {/* Top Insight */}
        {insights.length > 0 && (
          <InsightCard insight={insights[0]} />
        )}

        {/* Trending Areas (from Market Pulse) */}
        {allTrending.length > 0 && (
          <TrendingAreasSection
            items={allTrending}
            expanded={expandedSection === 'trending'}
            onToggle={() => setExpandedSection(expandedSection === 'trending' ? null : 'trending')}
          />
        )}

        {/* Upcoming Catalysts */}
        {catalysts?.catalysts?.length > 0 && (
          <CatalystEventsSection
            catalysts={catalysts.catalysts}
            matchedCriteria={catalysts.matchedCriteria}
            expanded={expandedSection === 'catalysts'}
            onToggle={() => setExpandedSection(expandedSection === 'catalysts' ? null : 'catalysts')}
          />
        )}

        {/* Top Recommended Buyers */}
        {recommendedBuyers.length > 0 && (
          <RecommendedBuyersSection
            buyers={recommendedBuyers.slice(0, 3)}
            expanded={expandedSection === 'buyers'}
            onToggle={() => setExpandedSection(expandedSection === 'buyers' ? null : 'buyers')}
          />
        )}

        {/* CTA */}
        <Link
          to="/seller/deal-intelligence"
          className="flex items-center justify-center gap-2 w-full py-2.5 text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors border border-emerald-200"
        >
          <Sparkles className="w-4 h-4" />
          View Full Analysis
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

// Trend indicator component
function TrendIndicator({ trend, trendPct }) {
  const config = {
    rising: { icon: TrendingUp, color: 'text-green-600', bg: 'bg-green-50', label: 'Rising' },
    declining: { icon: TrendingDown, color: 'text-red-600', bg: 'bg-red-50', label: 'Declining' },
    stable: { icon: Minus, color: 'text-gray-600', bg: 'bg-gray-50', label: 'Stable' },
  };
  const { icon: Icon, color, bg, label } = config[trend] || config.stable;

  return (
    <div className={`flex items-center gap-1 px-2 py-1 ${bg} rounded-full`}>
      <Icon className={`w-3.5 h-3.5 ${color}`} />
      <span className={`text-xs font-medium ${color}`}>
        {trendPct ? `${trendPct > 0 ? '+' : ''}${trendPct}%` : label}
      </span>
    </div>
  );
}

// Valuation card component
function ValuationCard({ valuation }) {
  const { upfront, total, confidence, comparableCount } = valuation;

  const confidenceConfig = {
    high: { color: 'text-green-600', label: 'High confidence' },
    medium: { color: 'text-amber-600', label: 'Medium confidence' },
    low: { color: 'text-gray-500', label: 'Low confidence' },
  };
  const conf = confidenceConfig[confidence] || confidenceConfig.low;

  return (
    <div className="bg-white rounded-lg p-4 border border-gray-100">
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
          Estimated Upfront Range
        </p>
        <span className={`text-xs ${conf.color}`}>{conf.label}</span>
      </div>
      <p className="text-xs text-gray-400 mb-3">Based on comparable deals with similar assets</p>

      {upfront ? (
        <>
          <div className="flex items-baseline gap-2 mb-1">
            <span className="text-3xl font-bold text-gray-900">
              ${upfront.mid}M
            </span>
            <span className="text-sm text-gray-500">
              median
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
            <span>${upfront.low}M</span>
            <div className="flex-1 h-1.5 bg-gray-100 rounded-full relative">
              <div
                className="absolute inset-y-0 bg-gradient-to-r from-emerald-400 to-green-500 rounded-full"
                style={{
                  left: '25%',
                  right: '25%',
                }}
              />
              <div
                className="absolute top-1/2 -translate-y-1/2 w-2 h-2 bg-emerald-600 rounded-full border-2 border-white shadow"
                style={{ left: '50%', marginLeft: '-4px' }}
              />
            </div>
            <span>${upfront.high}M</span>
          </div>
        </>
      ) : (
        <p className="text-sm text-gray-500">Insufficient data for valuation</p>
      )}

      {total && (
        <div className="pt-2 border-t border-gray-100">
          <p className="text-xs text-gray-500">
            Total deal value: <span className="font-medium text-gray-700">${total.low}M - ${total.high}M</span>
          </p>
        </div>
      )}

      <p className="text-xs text-gray-400 mt-2">
        Based on {comparableCount} comparable deals
      </p>
    </div>
  );
}

// Comparable deals section
function ComparableDealsSection({ deals, expanded, onToggle }) {
  const displayDeals = expanded ? deals : deals.slice(0, 2);

  return (
    <div>
      <button
        onClick={onToggle}
        className="flex items-center justify-between w-full text-left mb-2"
      >
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-emerald-500" />
          <div>
            <p className="text-sm font-medium text-gray-700">Similar Deals</p>
            <p className="text-xs text-gray-500">Historical transactions with similar TA, modality & stage</p>
          </div>
        </div>
        {deals.length > 2 && (
          <span className="text-xs text-emerald-600 flex items-center gap-1">
            {expanded ? 'Show less' : `+${deals.length - 2} more`}
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </span>
        )}
      </button>
      <div className="space-y-2">
        {displayDeals.map((deal, index) => (
          <div
            key={deal.id || index}
            className="p-3 bg-white rounded-lg border border-gray-100 text-sm"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">
                  {deal.headline || `${deal.buyer} → ${deal.seller}`}
                </p>
                <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                  {deal.therapeutic_area && <span>{deal.therapeutic_area}</span>}
                  {deal.modality && <span>• {deal.modality}</span>}
                  {deal.deal_date && (
                    <span>• {new Date(deal.deal_date).getFullYear()}</span>
                  )}
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                {deal.upfront ? (
                  <p className="font-semibold text-gray-900">
                    ${formatValue(deal.upfront)}
                  </p>
                ) : deal.total ? (
                  <p className="font-semibold text-gray-900">
                    ${formatValue(deal.total)}
                  </p>
                ) : null}
                <p className="text-xs text-emerald-600">{deal.similarityScore}% match</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Insight card component
function InsightCard({ insight }) {
  const typeConfig = {
    market_timing: { icon: Clock, color: 'text-blue-600', bg: 'bg-blue-50' },
    emphasis: { icon: Target, color: 'text-purple-600', bg: 'bg-purple-50' },
    buyer_fit: { icon: Building2, color: 'text-indigo-600', bg: 'bg-indigo-50' },
    valuation_driver: { icon: TrendingUp, color: 'text-green-600', bg: 'bg-green-50' },
    risk_factor: { icon: AlertCircle, color: 'text-amber-600', bg: 'bg-amber-50' },
    opportunity: { icon: Sparkles, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  };
  const config = typeConfig[insight.type] || typeConfig.opportunity;
  const Icon = config.icon;

  return (
    <div className={`p-3 ${config.bg} rounded-lg border border-opacity-50`}>
      <div className="flex items-start gap-2">
        <Icon className={`w-4 h-4 ${config.color} flex-shrink-0 mt-0.5`} />
        <div>
          <p className={`text-xs font-semibold ${config.color} mb-0.5`}>
            {insight.title}
          </p>
          <p className="text-sm text-gray-700">
            {insight.description}
          </p>
          {insight.recommendation && (
            <p className="text-xs text-gray-500 mt-1.5 flex items-center gap-1">
              <Lightbulb className="w-3 h-3" />
              {insight.recommendation}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// Trending areas section
function TrendingAreasSection({ items, expanded, onToggle }) {
  const displayItems = expanded ? items : items.slice(0, 2);

  return (
    <div>
      <button
        onClick={onToggle}
        className="flex items-center justify-between w-full text-left mb-2"
      >
        <div className="flex items-center gap-2">
          <Flame className="w-4 h-4 text-orange-500" />
          <div>
            <p className="text-sm font-medium text-gray-700">Hot Focus Areas</p>
            <p className="text-xs text-gray-500">Therapeutic areas and platforms with high buyer interest</p>
          </div>
        </div>
        {items.length > 2 && (
          <span className="text-xs text-orange-600 flex items-center gap-1">
            {expanded ? 'Show less' : `+${items.length - 2} more`}
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </span>
        )}
      </button>
      <div className="flex flex-wrap gap-2">
        {displayItems.map((item, index) => (
          <span
            key={index}
            className="inline-flex items-center gap-1 px-2.5 py-1 bg-white border border-orange-200 rounded-full text-sm"
          >
            {item.type === 'therapeutic' ? (
              <Target className="w-3 h-3 text-indigo-500" />
            ) : (
              <Zap className="w-3 h-3 text-purple-500" />
            )}
            <span className="font-medium text-gray-800">
              {item.area || item.platform}
            </span>
            <Flame className="w-3 h-3 text-orange-500" />
          </span>
        ))}
      </div>
    </div>
  );
}

// Recommended buyers section
function RecommendedBuyersSection({ buyers, expanded, onToggle }) {
  const [expandedBuyer, setExpandedBuyer] = useState(null);

  // Convert match score to star rating and label
  const getMatchDisplay = (score, urgencyLevel) => {
    if (score >= 80 || urgencyLevel === 'high') {
      return { stars: 5, label: 'Strong Match', color: 'text-green-600', bg: 'bg-green-50' };
    } else if (score >= 60 || urgencyLevel === 'medium') {
      return { stars: 4, label: 'Good Match', color: 'text-emerald-600', bg: 'bg-emerald-50' };
    } else if (score >= 40) {
      return { stars: 3, label: 'Potential', color: 'text-amber-600', bg: 'bg-amber-50' };
    } else {
      return { stars: 2, label: 'Exploring', color: 'text-gray-500', bg: 'bg-gray-50' };
    }
  };

  // Render star rating
  const StarRating = ({ count }) => (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          className={`w-3 h-3 ${star <= count ? 'text-amber-400' : 'text-gray-200'}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-indigo-500" />
          <div>
            <p className="text-sm font-medium text-gray-700">Active Acquirers</p>
            <p className="text-xs text-gray-500">Based on deal activity and therapeutic focus</p>
          </div>
        </div>
        <Link
          to="/seller/deal-intelligence"
          className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
        >
          View all
          <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
      <div className="space-y-2">
        {buyers.map((buyer, index) => {
          const matchDisplay = getMatchDisplay(buyer.matchScore, buyer.urgencyLevel);
          const isExpanded = expandedBuyer === buyer.companyName;
          const hasRelevantDeals = buyer.relevantDeals && buyer.relevantDeals.length > 0;

          return (
            <div
              key={buyer.buyerId || index}
              className="bg-white rounded-lg border border-gray-100 overflow-hidden"
            >
              <div className="flex items-center justify-between p-2.5">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-4 h-4 text-indigo-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 text-sm truncate">{buyer.companyName}</p>
                    <p className="text-xs text-gray-500">{buyer.dealHistory}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="text-right">
                    <div className="flex items-center gap-1.5">
                      <StarRating count={matchDisplay.stars} />
                      <span className={`text-xs font-medium ${matchDisplay.color}`}>
                        {matchDisplay.label}
                      </span>
                    </div>
                  </div>
                  {hasRelevantDeals && (
                    <button
                      onClick={() => setExpandedBuyer(isExpanded ? null : buyer.companyName)}
                      className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded transition-colors"
                      title="View recent deals"
                    >
                      {isExpanded ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </button>
                  )}
                </div>
              </div>

              {/* Expanded: Show relevant deals */}
              {isExpanded && hasRelevantDeals && (
                <div className="px-2.5 pb-2.5 pt-0">
                  <div className="bg-gray-50 rounded-lg p-2 space-y-1.5">
                    <p className="text-xs font-medium text-gray-500 mb-1">Recent Relevant Deals:</p>
                    {buyer.relevantDeals.slice(0, 3).map((deal, i) => (
                      <div key={i} className="text-xs text-gray-600 flex items-start gap-1">
                        <span className="text-gray-400 mt-0.5">•</span>
                        <div className="flex-1">
                          <span className="text-gray-700">{deal.headline}</span>
                          {deal.value && (
                            <span className="text-emerald-600 ml-1">
                              (${deal.value >= 1000 ? `${(deal.value / 1000).toFixed(1)}B` : `${Math.round(deal.value)}M`})
                            </span>
                          )}
                          {deal.date && (
                            <span className="text-gray-400 ml-1">
                              {new Date(deal.date).getFullYear()}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* Match explanation */}
      <p className="text-xs text-gray-400 mt-2 text-center">
        Match based on recent acquisitions in your therapeutic area and modality
      </p>
    </div>
  );
}

// Deal Alerts Section - "有人刚在你的 TA 做了交易"
function DealAlertsSection({ alerts, expanded, onToggle }) {
  const displayAlerts = expanded ? alerts : alerts.slice(0, 2);
  const highRelevanceCount = alerts.filter(a => a.relevance === 'high').length;

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3 border border-blue-100">
      <button
        onClick={onToggle}
        className="flex items-center justify-between w-full text-left mb-2"
      >
        <div className="flex items-center gap-2">
          <Bell className="w-4 h-4 text-blue-600" />
          <div>
            <p className="text-sm font-medium text-gray-900">Recent Deals in Your Space</p>
            <p className="text-xs text-gray-500">Transactions in your therapeutic area in the last 90 days</p>
          </div>
          {highRelevanceCount > 0 && (
            <span className="px-1.5 py-0.5 bg-blue-600 text-white text-xs font-medium rounded-full">
              {highRelevanceCount} relevant
            </span>
          )}
        </div>
        {alerts.length > 2 && (
          <span className="text-xs text-blue-600 flex items-center gap-1">
            {expanded ? 'Show less' : `+${alerts.length - 2} more`}
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </span>
        )}
      </button>
      <div className="space-y-2">
        {displayAlerts.map((alert, index) => (
          <div
            key={alert.id || index}
            className={`p-2.5 bg-white rounded-lg border text-sm ${
              alert.relevance === 'high' ? 'border-blue-200' : 'border-gray-100'
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">{alert.headline}</p>
                <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                  <span>{alert.daysAgo === 0 ? 'Today' : `${alert.daysAgo}d ago`}</span>
                  {alert.matchReasons.map((reason, i) => (
                    <span key={i} className="text-blue-600">• {reason}</span>
                  ))}
                </div>
              </div>
              {alert.value && (
                <span className="font-semibold text-gray-900 flex-shrink-0">
                  ${formatValue(alert.value)}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Valuation Trend Card - "过去6个月 ADC 估值涨了30%"
function ValuationTrendCard({ trend }) {
  const { trendPct, trendDescription, modalityTrend, therapeuticArea, totalDeals } = trend;

  const getTrendColor = (t) => {
    if (t === 'rising') return { bg: 'bg-green-50', text: 'text-green-700', icon: TrendingUp };
    if (t === 'declining') return { bg: 'bg-red-50', text: 'text-red-700', icon: TrendingDown };
    return { bg: 'bg-gray-50', text: 'text-gray-700', icon: Minus };
  };

  const mainTrend = getTrendColor(trend.trend);
  const MainIcon = mainTrend.icon;

  return (
    <div className={`${mainTrend.bg} rounded-lg p-3 border border-opacity-50`}>
      <div className="flex items-center gap-2 mb-2">
        <Activity className="w-4 h-4 text-purple-600" />
        <div>
          <p className="text-sm font-medium text-gray-900">Valuation Trends</p>
          <p className="text-xs text-gray-500">How deal values changed over the last 6 months</p>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className={`flex items-center gap-1.5 px-2.5 py-1 ${mainTrend.bg} rounded-full border`}>
          <MainIcon className={`w-4 h-4 ${mainTrend.text}`} />
          <span className={`text-sm font-semibold ${mainTrend.text}`}>
            {trendPct > 0 ? '+' : ''}{trendPct}%
          </span>
        </div>
        <p className="text-sm text-gray-600">{trendDescription}</p>
      </div>

      {modalityTrend && (
        <div className="mt-2 pt-2 border-t border-gray-200">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-gray-500">{modalityTrend.modality}:</span>
            <span className={`font-medium ${getTrendColor(modalityTrend.trend).text}`}>
              {modalityTrend.description}
            </span>
          </div>
        </div>
      )}

      <p className="text-xs text-gray-400 mt-2">
        Based on {totalDeals} deals in {therapeuticArea}
      </p>
    </div>
  );
}

// Buyer-Asset Match Section - "BMS 刚收购了类似你的 asset"
function BuyerAssetMatchSection({ matches, expanded, onToggle }) {
  const displayMatches = expanded ? matches : matches.slice(0, 2);

  return (
    <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-3 border border-amber-100">
      <button
        onClick={onToggle}
        className="flex items-center justify-between w-full text-left mb-2"
      >
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-amber-600" />
          <div>
            <p className="text-sm font-medium text-gray-900">Buyers Acquiring Similar Assets</p>
            <p className="text-xs text-gray-500">Companies that acquired assets like yours in the last 12 months</p>
          </div>
        </div>
        {matches.length > 2 && (
          <span className="text-xs text-amber-600 flex items-center gap-1">
            {expanded ? 'Show less' : `+${matches.length - 2} more`}
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </span>
        )}
      </button>
      <div className="space-y-2">
        {displayMatches.map((match, index) => (
          <div
            key={match.buyer || index}
            className="p-2.5 bg-white rounded-lg border border-gray-100"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  match.signalStrength === 'strong' ? 'bg-amber-100' : 'bg-gray-100'
                }`}>
                  <Building2 className={`w-4 h-4 ${
                    match.signalStrength === 'strong' ? 'text-amber-600' : 'text-gray-500'
                  }`} />
                </div>
                <div>
                  <p className="font-medium text-gray-900 text-sm">{match.buyer}</p>
                  <p className="text-xs text-gray-500">{match.alertMessage}</p>
                </div>
              </div>
              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                match.signalStrength === 'strong' ? 'bg-amber-100 text-amber-700' :
                match.signalStrength === 'moderate' ? 'bg-blue-100 text-blue-700' :
                'bg-gray-100 text-gray-600'
              }`}>
                {match.matchScore}% match
              </span>
            </div>
            {match.recentDeals && match.recentDeals.length > 0 && expanded && (
              <div className="mt-2 pt-2 border-t border-gray-100">
                <p className="text-xs text-gray-500 mb-1">Recent acquisitions:</p>
                {match.recentDeals.slice(0, 2).map((deal, i) => (
                  <p key={i} className="text-xs text-gray-600 truncate">
                    • {deal.headline} {deal.value ? `($${formatValue(deal.value)})` : ''}
                  </p>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Helper function to format deal values
function formatValue(valueInMillions) {
  if (valueInMillions >= 1000) {
    return `${(valueInMillions / 1000).toFixed(1)}B`;
  }
  return `${Math.round(valueInMillions)}M`;
}

// Catalyst Events Section - Upcoming clinical catalysts
function CatalystEventsSection({ catalysts, matchedCriteria, expanded, onToggle }) {
  const displayCatalysts = expanded ? catalysts.slice(0, 8) : catalysts.slice(0, 3);
  const highRelevanceCount = catalysts.filter(c => c.relevance_score >= 50).length;

  // Format date for display
  const formatCatalystDate = (dateStr, precision) => {
    if (!dateStr) return 'TBD';
    const date = new Date(dateStr);
    if (precision === 'quarter') {
      const quarter = Math.ceil((date.getMonth() + 1) / 3);
      return `Q${quarter} ${date.getFullYear()}`;
    }
    if (precision === 'month') {
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Calculate days until catalyst
  const getDaysUntil = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  return (
    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-3 border border-purple-100">
      <button
        onClick={onToggle}
        className="flex items-center justify-between w-full text-left mb-2"
      >
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-purple-600" />
          <div>
            <p className="text-sm font-medium text-gray-900">Upcoming Catalysts</p>
            <p className="text-xs text-gray-500">
              Clinical data readouts relevant to your {matchedCriteria?.therapeuticArea || 'space'}
            </p>
          </div>
          {highRelevanceCount > 0 && (
            <span className="px-1.5 py-0.5 bg-purple-600 text-white text-xs font-medium rounded-full">
              {highRelevanceCount} relevant
            </span>
          )}
        </div>
        {catalysts.length > 3 && (
          <span className="text-xs text-purple-600 flex items-center gap-1">
            {expanded ? 'Show less' : `+${catalysts.length - 3} more`}
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </span>
        )}
      </button>
      <div className="space-y-2">
        {displayCatalysts.map((catalyst, index) => {
          const daysUntil = getDaysUntil(catalyst.expected_date);
          const isImminent = daysUntil !== null && daysUntil <= 30;
          const relevanceScore = catalyst.relevance_score || 0;

          return (
            <div
              key={catalyst.id || index}
              className={`p-2.5 bg-white rounded-lg border text-sm ${
                relevanceScore >= 50 ? 'border-purple-200' : 'border-gray-100'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Beaker className="w-3.5 h-3.5 text-purple-500 flex-shrink-0" />
                    <p className="font-medium text-gray-900 truncate">
                      {catalyst.company_name}
                    </p>
                  </div>
                  <p className="text-xs text-gray-600 line-clamp-2">
                    {catalyst.event_title}
                  </p>
                  <div className="flex items-center gap-2 mt-1.5 text-xs text-gray-500">
                    {catalyst.therapeutic_area && (
                      <span className="inline-flex items-center gap-0.5">
                        <Target className="w-3 h-3" />
                        {catalyst.therapeutic_area}
                      </span>
                    )}
                    {catalyst.modality && (
                      <span className="inline-flex items-center gap-0.5">
                        <Zap className="w-3 h-3" />
                        {catalyst.modality}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className={`text-xs font-medium ${
                    isImminent ? 'text-orange-600' : 'text-gray-600'
                  }`}>
                    {formatCatalystDate(catalyst.expected_date, catalyst.date_precision)}
                  </div>
                  {daysUntil !== null && (
                    <div className={`text-xs mt-0.5 ${
                      isImminent ? 'text-orange-500 font-medium' : 'text-gray-400'
                    }`}>
                      {daysUntil <= 0 ? 'Imminent' : `${daysUntil}d away`}
                    </div>
                  )}
                  {catalyst.significance_score >= 8 && (
                    <span className="inline-flex mt-1 px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded">
                      Major
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {catalysts.length > 0 && (
        <p className="text-xs text-gray-400 mt-2 text-center">
          Track competitor data readouts to time your outreach
        </p>
      )}
    </div>
  );
}
